/**
 * TaskDetailPage — view, edit status/progress, and update a task
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Edit2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTMAuth } from '@/contexts/TMAuthContext';
import { getTask, updateTask, deleteTask, getApprovedTMUsers } from '@/services/tmService';
import type { Task, TaskStatus, TaskPriority, TMUser } from '@/types/tm.types';
import {
  TASK_STATUS_COLORS, TASK_STATUS_LABELS, TASK_STATUSES,
  TASK_PRIORITY_COLORS, TASK_PRIORITY_LABELS, TASK_PRIORITIES,
} from '@/types/tm.types';
import { cn } from '@/utils/cn';

function daysUntil(date: Date | null) {
  if (!date) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86400000);
}

export default function TaskDetailPage() {
  const { id }         = useParams<{ id: string }>();
  const navigate       = useNavigate();
  const { tmUser, isTMAdmin } = useTMAuth();
  const [task,    setTask]    = useState<Task | null>(null);
  const [members, setMembers] = useState<TMUser[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);

  // Editable fields
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [status,      setStatus]      = useState<TaskStatus>('todo');
  const [priority,    setPriority]    = useState<TaskPriority>('medium');
  const [progress,    setProgress]    = useState(0);
  const [dueDate,     setDueDate]     = useState('');
  const [assignedToUid, setAssignedToUid] = useState('');

  useEffect(() => {
    if (!id) return;
    getTask(id).then(t => {
      if (!t) return;
      setTask(t);
      setTitle(t.title);
      setDescription(t.description ?? '');
      setStatus(t.status);
      setPriority(t.priority);
      setProgress(t.progress);
      setDueDate(t.dueDate ? t.dueDate.toISOString().split('T')[0] : '');
      setAssignedToUid(t.assignedTo?.uid ?? '');
    });
    if (isTMAdmin) getApprovedTMUsers().then(setMembers);
  }, [id, isTMAdmin]);

  const isAssignee = task?.assignedTo?.uid === tmUser?.uid;
  const canEdit    = isTMAdmin || isAssignee;

  const handleSave = async () => {
    if (!id || !task) return;
    setSaving(true);
    try {
      const assignee = members.find(m => m.uid === assignedToUid);
      await updateTask(id, {
        title,
        description: description || null,
        status,
        priority,
        progress,
        dueDate:    dueDate ? new Date(dueDate) : null,
        assignedTo: assignee ? { uid: assignee.uid, name: assignee.name, email: assignee.email }
                             : task.assignedTo,
      });
      setTask(prev => prev ? { ...prev, title, description: description || null, status, priority, progress } : prev);
      toast.success('Task updated');
      setEditing(false);
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!id || !confirm('Delete this task?')) return;
    await deleteTask(id);
    toast.success('Task deleted');
    navigate('/tm/tasks');
  };

  // Quick status update (no edit mode needed)
  const handleStatusChange = async (s: TaskStatus) => {
    if (!id) return;
    setStatus(s);
    await updateTask(id, { status: s });
    toast.success('Status updated');
  };

  if (!task) return (
    <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Loading…</div>
  );

  const d = daysUntil(task.dueDate);
  const inputCls = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400';

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex items-center gap-2">
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              <Edit2 className="h-4 w-4" /> Edit
            </button>
          )}
          {editing && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold active:scale-95 transition-all disabled:opacity-60">
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          {isTMAdmin && (
            <button onClick={handleDelete}
              className="p-2 rounded-xl text-red-500 hover:bg-red-50 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">

        {/* Title */}
        {editing ? (
          <input value={title} onChange={e => setTitle(e.target.value)} className={inputCls} />
        ) : (
          <h1 className="text-lg font-bold text-gray-900">{task.title}</h1>
        )}

        {/* Status pills */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Status</p>
          <div className="flex gap-2 flex-wrap">
            {TASK_STATUSES.map(s => (
              <button key={s} type="button"
                onClick={() => canEdit && handleStatusChange(s)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-semibold border transition-all',
                  status === s
                    ? cn(TASK_STATUS_COLORS[s], 'border-current ring-2 ring-offset-1 ring-current/30')
                    : 'border-gray-200 text-gray-400',
                  !canEdit && 'cursor-default',
                )}>
                {TASK_STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Priority */}
        {editing ? (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Priority</p>
            <div className="flex gap-2 flex-wrap">
              {TASK_PRIORITIES.map(p => (
                <button key={p} type="button" onClick={() => setPriority(p)}
                  className={cn('px-3 py-1.5 rounded-full text-xs font-semibold border transition-all capitalize',
                    priority === p
                      ? cn(TASK_PRIORITY_COLORS[p], 'ring-2 ring-offset-1 ring-current/30')
                      : 'border-gray-200 text-gray-400')}>
                  {TASK_PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <span className={cn('inline-block text-xs font-bold px-3 py-1 rounded-full', TASK_PRIORITY_COLORS[task.priority])}>
            {TASK_PRIORITY_LABELS[task.priority]} Priority
          </span>
        )}

        {/* Progress */}
        {canEdit ? (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Progress</p>
              <span className="text-sm font-bold text-indigo-600">{progress}%</span>
            </div>
            <input type="range" min={0} max={100} step={5} value={progress}
              onChange={e => setProgress(Number(e.target.value))}
              className="w-full accent-indigo-600" />
          </div>
        ) : task.progress > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Progress</p>
              <span className="text-sm font-bold text-indigo-600">{task.progress}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100">
              <div className="h-2 rounded-full bg-indigo-400 transition-all" style={{ width: `${task.progress}%` }} />
            </div>
          </div>
        ) : null}

        {/* Description */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Description</p>
          {editing ? (
            <textarea rows={4} value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Add details…" className={inputCls} />
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.description ?? '—'}</p>
          )}
        </div>

        {/* Meta */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Assigned to</p>
            {editing && isTMAdmin ? (
              <select value={assignedToUid} onChange={e => setAssignedToUid(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-indigo-400">
                <option value="">Unassigned</option>
                {members.map(m => <option key={m.uid} value={m.uid}>{m.name}</option>)}
              </select>
            ) : (
              <p className="text-sm font-medium text-gray-800">{task.assignedTo?.name ?? 'Unassigned'}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Assigned by</p>
            <p className="text-sm font-medium text-gray-800">{task.assignedBy?.name ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Due date</p>
            {editing ? (
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            ) : (
              <p className={cn('text-sm font-medium',
                d !== null && d < 0 ? 'text-red-500' : d !== null && d <= 3 ? 'text-amber-600' : 'text-gray-800')}>
                {task.dueDate
                  ? task.dueDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '—'}
                {d !== null && d < 0 && ` (${Math.abs(d)}d overdue)`}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Reminder</p>
            <p className="text-sm font-medium text-gray-800">
              {task.reminderDays > 0 ? `${task.reminderDays} day${task.reminderDays !== 1 ? 's' : ''} before` : 'None'}
            </p>
          </div>
        </div>

        {task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {task.tags.map(tag => (
              <span key={tag} className="text-xs bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-full font-medium">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
